import { useState, type FormEvent } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight, KeyRound } from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AuthLayout } from "@/components/auth/auth-layout"
import { describeAuthError } from "@/lib/auth-errors"
import { Field } from "@/components/workspace/primitives"
import { authClient } from "@/lib/auth-client"

export const Route = createFileRoute("/reset-password")({
  validateSearch: z.object({ token: z.string().optional(), error: z.string().optional() }),
  component: ResetPassword,
})

function ResetPassword() {
  const { token, error: linkError } = Route.useSearch()
  const navigate = useNavigate()
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!token) return
    if (password.length < 8) return setError("Use at least 8 characters for your password.")
    if (password !== confirm) return setError("The two passwords don’t match.")
    setBusy(true)
    const result = await authClient.resetPassword({ newPassword: password, token })
    setBusy(false)
    if (result.error) return setError(describeAuthError(result.error))
    toast.success("Password updated. Sign in with your new password.")
    await navigate({ to: "/sign-in" })
  }

  if (!token || linkError) {
    return (
      <AuthLayout mark={<KeyRound size={23} />} title="This reset link isn’t valid" subtitle="Reset links work once and expire after a short time. Request a new one to continue.">
        <Button className="w-full" asChild>
          <Link to="/forgot-password">Request a new link</Link>
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout mark={<KeyRound size={23} />} title="Choose a new password" subtitle="Your new password replaces the old one for every device.">
      <form onSubmit={submit} className="ws-auth-fields">
        <Field label="New password" hint="Use at least 8 characters.">
          {(id) => <Input id={id} type="password" value={password} required autoComplete="new-password" autoFocus onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        <Field label="Confirm new password">{(id) => <Input id={id} type="password" value={confirm} required autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)} />}</Field>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button className="w-full" size="lg" type="submit" disabled={busy}>
          Update password
          <ArrowRight />
        </Button>
      </form>
    </AuthLayout>
  )
}
